/**
 * 守护服务 · 医疗健康资源矩阵（7×24）
 *
 * 产品目标：医疗资源是企业家群体最看重的硬通货，这一屏承担「证明资源真实可用」的任务。
 *
 * 与原型的关键差别（本次改造的重点）：
 *   1. 原型里「3 日内可约」「已开通」「年度含 2 次」全是写死的假状态；
 *      现在 availabilityText / availableCount / badgeTone 由后端按号源库存实时算出，
 *      页面原样使用，不在前端重算、不写死任何可约文案；
 *   2. 资源卡的「尽调结论」是可举证的真实字段，用来支撑「资源真实可用」；
 *   3. 预约是一次真实的下单（容量在服务端事务内扣减），约满/过期时展示后端错误文案；
 *   4. 工单状态机与履约时间轴来自服务端，不丢、不刷。
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ServiceBooking,
  ServiceCategoryKey,
  ServiceMatrixItem,
  ServiceOverview,
  ServiceResource,
  ServiceSlot,
  SlotStatus,
} from '@hew3/shared';
import {
  isSlotBookable,
  resolveColor,
  slotStatusLabels,
  slotStatusNotes,
  slotStatusOf,
} from '@hew3/shared';
import { ApiError, api } from '../../lib/api.ts';
import { formatDateTime, formatSlot } from '../../lib/format.ts';
import { useUi } from '../../store/ui.ts';
import {
  AlertStrip,
  Avatar,
  Badge,
  Button,
  Card,
  Kpi,
  Sheet,
  StateBox,
  Timeline,
} from '../../components/ui.tsx';

/** 徽章色阶：与 ui.tsx 的 BadgeTone 对齐，页面内做状态→色阶映射时用得上。 */
type BadgeTone = 'h' | 'e' | 'w' | 'ok' | 'warn' | 'danger' | 'ghost';

/* ================================================================== *
 * 局部视图常量与工具（只在页面内使用，不改共享层）
 * ================================================================== */

/**
 * 服务类别图标：与原型 assets/data.js 的 s.icons 逐条一致，
 * 用 (category, iconName) 双键查表，后端换了图标名也不会退化成空白图标。
 */
const serviceIcons: Record<string, { byName: Record<string, string>; fallback: string }> = {
  hospital: {
    byName: { hospital: '<path d="M4 21V8l8-5 8 5v13"/><path d="M9 21v-6h6v6"/><path d="M12 3v5M9.5 5.5h5"/>' },
    fallback: '<path d="M4 21V8l8-5 8 5v13"/><path d="M9 21v-6h6v6"/>',
  },
  mdt: {
    byName: { mdt: '<circle cx="12" cy="8" r="3"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/><path d="M3 12h2M19 12h2"/>' },
    fallback: '<circle cx="12" cy="8" r="3"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/>',
  },
  exam: {
    byName: { test: '<path d="M9 3v6.5L5 20a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 20l-4-10.5V3"/><path d="M8 3h8M8 14h8"/>' },
    fallback: '<path d="M9 3v6.5L5 20a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 20l-4-10.5V3"/><path d="M8 3h8"/>',
  },
  spa: {
    byName: { spa: '<path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11z"/><path d="M12 8v6M9 11h6"/>' },
    fallback: '<path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11z"/>',
  },
  global: {
    byName: { global: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z"/>' },
    fallback: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/>',
  },
  sos: {
    byName: { sos: '<path d="M12 2v20M2 12h20"/>' },
    fallback: '<path d="M12 2v20M2 12h20"/>',
  },
};

const defaultServiceIcon = '<circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/>';

function iconPathOf(key: ServiceCategoryKey, icon: string): string {
  const group = serviceIcons[key];
  if (!group) return defaultServiceIcon;
  return group.byName[icon] ?? group.fallback;
}

/**
 * 号源状态的徽章色阶：**只做颜色映射，不做状态判定**。
 * 状态本身（含「已过期」）由 `@hew3/shared` 的唯一判据 `slotStatusOf()` 给出，
 * 中文名与不可约原因说明同样来自共享层（`slotStatusLabels` / `slotStatusNotes`），
 * 前端不另写一套说法 —— 否则同一个 key 会出现两种中文名。
 */
const slotStatusTones: Record<SlotStatus, 'ok' | 'warn' | 'ghost'> = {
  open: 'ok',
  expired: 'ghost',
  full: 'warn',
  closed: 'warn',
};

/**
 * 页面打开期间走时：号源一旦到点即自动转为不可约，
 * 每分钟用**与后端同一个判据**复算一次，既不频繁重渲染，也不会长期显示过期的「可预约」。
 */
function useNowMs(intervalMs = 60_000): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return nowMs;
}

/* ================================================================== *
 * 页面
 * ================================================================== */

interface BookingTarget {
  resource: ServiceResource;
  slot: ServiceSlot;
}

export function ServicePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openSheet = useUi((s) => s.openSheet);
  const showToast = useUi((s) => s.showToast);

  /** 矩阵格子点选后就地筛选资源；null = 全部类别。 */
  const [category, setCategory] = useState<ServiceCategoryKey | null>(null);
  /** 预约弹层：选中的资源 + 号源。 */
  const [bookingTarget, setBookingTarget] = useState<BookingTarget | null>(null);
  const [note, setNote] = useState('');
  /** 后端下发的失败原因（约满/过期），必须原样展示在弹层内。 */
  const [sheetError, setSheetError] = useState<string | null>(null);
  /** 二次确认：正在等待确认取消的工单 id。 */
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const nowMs = useNowMs();

  const overviewQuery = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<ServiceOverview>('/api/services/overview'),
  });

  const bookingsQuery = useQuery({
    queryKey: ['services', 'bookings'],
    queryFn: () => api.get<ServiceBooking[]>('/api/services/bookings'),
  });

  const createBooking = useMutation({
    mutationFn: (vars: { resourceId: string; slotId: string; note: string }) =>
      api.post<ServiceBooking>('/api/services/bookings', vars),
    onSuccess: (booking) => {
      setBookingTarget(null);
      setNote('');
      setSheetError(null);
      showToast(`预约已提交：${booking.resourceName} · ${booking.slotLabel}，由专属健康管家受理`);
      void queryClient.invalidateQueries({ queryKey: ['services'] });
      void queryClient.invalidateQueries({ queryKey: ['services', 'bookings'] });
    },
    onError: (err) => setSheetError(err instanceof ApiError ? err.message : '预约提交失败，请稍后重试'),
  });

  const cancelBooking = useMutation({
    mutationFn: (id: string) => api.post<ServiceBooking>(`/api/services/bookings/${id}/cancel`),
    onSuccess: (booking) => {
      setCancelConfirmId(null);
      showToast(`已取消：${booking.resourceName}，号源已释放`);
      void queryClient.invalidateQueries({ queryKey: ['services'] });
      void queryClient.invalidateQueries({ queryKey: ['services', 'bookings'] });
    },
    onError: (err) => {
      setCancelConfirmId(null);
      showToast(err instanceof ApiError ? err.message : '取消失败，请稍后重试', 'error');
    },
  });

  const closeBookingSheet = () => {
    setBookingTarget(null);
    setNote('');
    setSheetError(null);
  };

  const reloadAll = () => {
    void overviewQuery.refetch();
    void bookingsQuery.refetch();
  };

  /* ---------------------------- 加载 / 失败 ---------------------------- */

  if (overviewQuery.isLoading) {
    return (
      <div className="page-pad">
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 196 }} />
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 220 }} />
      </div>
    );
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    return (
      <div className="page">
        <div className="scr-head">
          <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
            <svg viewBox="0 0 24 24">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="scr-title">
            守护服务<small>医疗健康资源矩阵 · 7×24</small>
          </div>
        </div>
        <StateBox
          kind="error"
          title="守护服务数据加载失败"
          desc="请检查网络后重试。若持续失败，可直接拨打专属健康管家电话。"
          action={
            <div className="row-flex">
              <Button small onClick={reloadAll}>
                重新加载
              </Button>
              <Link className="btn sm" to="/">
                返回首页
              </Link>
            </div>
          }
        />
      </div>
    );
  }

  /* ---------------------------- 数据 ---------------------------- */

  const data = overviewQuery.data;
  const { guardian, matrix, resources, activeSos, sla, admissionRule } = data;
  /** 工单以独立查询为准，首帧未回来时用总览内嵌的同一份数据，避免闪空。 */
  const bookings = bookingsQuery.data ?? data.bookings;

  const availableTotal = matrix.reduce((sum, item) => sum + item.availableCount, 0);

  const activeCategory = category ? (matrix.find((m) => m.key === category) ?? null) : null;
  const shownResources = category ? resources.filter((r) => r.category === category) : resources;

  const bookingSlot = bookingTarget ? bookingTarget.slot : null;
  const bookingRemaining = bookingSlot ? bookingSlot.capacity - bookingSlot.booked : 0;

  return (
    <div className="page">
      {/* ---------------- 返回头 ---------------- */}
      <div className="scr-head">
        <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
          <svg viewBox="0 0 24 24">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="scr-title">
          守护服务<small>医疗健康资源矩阵 · 7×24</small>
        </div>
      </div>

      {/* ---------------- 进行中的应急事件：必须置于页面顶部 ---------------- */}
      {activeSos && (
        <AlertStrip
          tone="danger"
          title={`应急响应进行中 · ${activeSos.statusLabel}`}
          sub={`处理人 ${activeSos.handlerName} · ${activeSos.etaText}`}
          onClick={() => openSheet('sos')}
        />
      )}

      {/* ---------------- 专属管家 ---------------- */}
      <div className="guard-card">
        <Avatar char={guardian.name.slice(0, 1)} />
        <div className="grow">
          <div className="gc-t">{guardian.name}</div>
          <div className="gc-s">
            {guardian.title}
            <br />
            {guardian.phone} · {guardian.slaText}
          </div>
        </div>
        <div className="row-flex" style={{ flex: '0 0 auto', gap: 6 }}>
          <Button small onClick={() => { window.location.href = `tel:${guardian.phone}`; }}>
            拨打
          </Button>
          <Button small variant="danger" onClick={() => openSheet('sos')}>
            应急 SOS
          </Button>
        </div>
      </div>

      {/* ---------------- 响应数据（服务端实测口径） ---------------- */}
      <Card title="响应与处置口径">
        <div className="grid3">
          <Kpi label="平均响应" value={sla.avgResponseText || '待评测'} tone="h" />
          <Kpi label="响应标准" value={sla.standardText || '以管家口径为准'} />
          <Kpi label="行业对比" value={sla.industryAvgText || '暂无实测对比'} />
        </div>
        <div className="sec-note" style={{ marginTop: 6 }}>
          已响应事件 {sla.respondedCount} 起
          {sla.measuredAt ? ` · 统计至 ${formatDateTime(sla.measuredAt)}` : ''}
        </div>
        <div className="note-src">
          <b>数据来源</b>
          {sla.measuredAt
            ? `：平台响应工单实测口径，样本为本人已响应事件 ${sla.respondedCount} 起。`
            : '：暂无实测样本，故不展示响应时长与行业对比数值；平台不引用未经验证的响应时长。'}
        </div>
      </Card>

      {/* ---------------- 六大服务矩阵 ---------------- */}
      <Card
        title="服务资源矩阵"
        more={
          <span className="badge-avail">
            <Badge tone={availableTotal > 0 ? 'ok' : 'warn'}>当前可约号源 {availableTotal} 个</Badge>
          </span>
        }
      >
        <div className="svc-grid">
          {matrix.map((item: ServiceMatrixItem) => {
            const clickable = item.availableCount > 0;
            const active = category === item.key;
            return (
              <button
                key={item.key}
                type="button"
                className="svc"
                disabled={!clickable}
                aria-pressed={active}
                onClick={() => setCategory(active ? null : item.key)}
                style={{
                  textAlign: 'left',
                  color: 'inherit',
                  font: 'inherit',
                  opacity: clickable ? 1 : 0.62,
                  cursor: clickable ? 'pointer' : 'default',
                  borderColor: active ? 'var(--h)' : undefined,
                }}
              >
                <div className="sv-i">
                  {/* 图标路径来自本页常量表（原型 assets/data.js 的原样移植），非用户输入 */}
                  <svg
                    viewBox="0 0 24 24"
                    style={{ stroke: resolveColor(item.color) }}
                    dangerouslySetInnerHTML={{ __html: iconPathOf(item.key, item.icon) }}
                  />
                </div>
                <div className="sv-t">{item.name}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5, lineHeight: 1.6 }}>{item.desc}</div>
                {/* 可约文案完全由后端按号源库存算出，前端不重算、不写死 */}
                <div className="badge-avail" style={{ marginTop: 9 }}>
                  <Badge tone={item.badgeTone}>{item.availabilityText}</Badge>
                </div>
              </button>
            );
          })}
        </div>
        <div className="note-src">
          <b>号源口径</b>：上方状态按各类别实时可约时段数与最早可约时间计算，随号源库存变化，不预先承诺。
          点选任一类别可在下方筛选该类资源与号源。
        </div>
      </Card>

      {/* ---------------- 资源与号源 ---------------- */}
      <Card
        title="资源与号源"
        more={
          activeCategory ? (
            <button type="button" className="card-more" onClick={() => setCategory(null)}>
              显示全部 ›
            </button>
          ) : (
            <span className="card-more">共 {resources.length} 个已准入资源</span>
          )
        }
      >
        {activeCategory && (
          <>
            <div className="row-flex" style={{ gap: 6, flexWrap: 'wrap' }}>
              <Badge tone="e">{activeCategory.name}</Badge>
              <Badge tone={activeCategory.badgeTone}>{activeCategory.availabilityText}</Badge>
              <span className="text-muted" style={{ fontSize: 11 }}>
                可约 {activeCategory.availableCount} 个时段
              </span>
            </div>
            <div className="sp12" />
          </>
        )}

        {shownResources.length === 0 ? (
          <StateBox
            kind="empty"
            title={category ? '该类资源暂未开通号源' : '暂无已开通的服务资源'}
            desc={
              category
                ? '可切换其它类别，或联系专属健康管家申请定向协调。'
                : '准入审核通过后会在此展示；如需紧急协调，可联系专属健康管家。'
            }
          />
        ) : (
          <div className="stack">
            {shownResources.map((resource: ServiceResource) => {
              const cat = matrix.find((m) => m.key === resource.category);
              return (
                <div key={resource.id} className="card tight" style={{ background: 'rgba(255,255,255,.02)' }}>
                  <div className="row between" style={{ alignItems: 'flex-start' }}>
                    <div className="grow">
                      <div className="li-t" style={{ fontSize: 13.5, fontWeight: 600 }}>
                        {resource.name}
                      </div>
                      <div className="text-tiny text-muted" style={{ marginTop: 4 }}>
                        {resource.institution} · {resource.city}
                      </div>
                    </div>
                    {cat && <Badge tone={cat.badgeTone}>{cat.availabilityText}</Badge>}
                  </div>

                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 7, lineHeight: 1.65 }}>
                    {resource.desc}
                  </div>

                  {/* 尽调结论 = 「资源真实可用」的可举证证据 */}
                  <div className="data-tag" style={{ marginTop: 8 }}>
                    尽调结论：{resource.dueDiligence}
                  </div>

                  <div className="divider" />

                  {resource.slots.length === 0 ? (
                    <div className="sec-note" style={{ marginTop: 0 }}>
                      当前无公开号源，可联系专属健康管家申请协调排期。
                    </div>
                  ) : (
                    resource.slots.map((slot) => {
                      /**
                       * 号源状态**不由前端判定**：这里调用的是后端同一条判据
                       * （`@hew3/shared` 的 `slotStatusOf`），只是把「现在」换成页面当前的时钟。
                       * 因此页面打开期间到点的号源会自动变成「已过期」，而不需要前端另写一份
                       * `startAt > now` —— 那种「第二份判据」正是本缺陷的成因
                       * （服务端漏了时间条件，把已过期号源标成 open，成员点了必然 409）。
                       */
                      const slotStatus = slotStatusOf(slot, nowMs);
                      // 「能不能点」与「显示成什么」是**同一个判据的两个答案**（不是两套判断）
                      const bookable = isSlotBookable(slot, nowMs);
                      const remaining = Math.max(0, slot.capacity - slot.booked);
                      const note = slotStatusNotes[slotStatus];
                      return (
                        <div key={slot.id} className="row between" style={{ gap: 10, padding: '7px 0' }}>
                          <div className="grow">
                            <div
                              className="mono-num"
                              style={{ fontSize: 12.5, fontWeight: 600, color: bookable ? 'var(--text)' : 'var(--muted)' }}
                            >
                              {formatSlot(slot.startAt)}
                            </div>
                            <div className="text-tiny text-muted" style={{ marginTop: 2 }}>
                              {slot.label} · 容量 {slot.capacity} 人 ·{' '}
                              {remaining > 0 ? `剩余 ${remaining} 个名额` : '名额已满'}
                            </div>
                            {/* 三种不可约原因各自说明（已过期 / 已约满 / 已停诊），不共用一句含糊文案 */}
                            {note && (
                              <div
                                className="text-tiny"
                                style={{ marginTop: 3, color: slotStatus === 'expired' ? 'var(--muted)' : 'var(--warn)' }}
                              >
                                {note}
                              </div>
                            )}
                          </div>
                          <div className="row-flex" style={{ flex: '0 0 auto', gap: 6 }}>
                            <Badge tone={slotStatusTones[slotStatus]}>{slotStatusLabels[slotStatus]}</Badge>
                            <Button
                              small
                              variant={bookable ? 'primary' : 'default'}
                              disabled={!bookable}
                              onClick={() => {
                                setSheetError(null);
                                setNote('');
                                setBookingTarget({ resource, slot });
                              }}
                            >
                              预约
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ---------------- 我的服务工单 ---------------- */}
      <Card
        title="我的服务工单"
        more={
          bookingsQuery.isFetching ? (
            <span className="card-more">同步中…</span>
          ) : (
            <span className="card-more">共 {bookings.length} 条</span>
          )
        }
      >
        {bookingsQuery.isError && (
          <div className="field-error">
            工单列表同步失败，展示的是最近一次结果。
            <button type="button" className="btn sm" style={{ marginLeft: 8 }} onClick={() => void bookingsQuery.refetch()}>
              重新加载
            </button>
          </div>
        )}

        {bookings.length === 0 ? (
          <StateBox kind="empty" title="暂无服务工单" desc="在上方选择资源与号源完成预约后，履约进度会在此逐步更新。" />
        ) : (
          <div className="stack">
            {bookings.map((booking) => {
              const cancelable =
                booking.status === 'pending' || booking.status === 'matched' || booking.status === 'scheduled';
              const confirming = cancelConfirmId === booking.id;
              const tone: BadgeTone =
                booking.status === 'done'
                  ? 'ok'
                  : booking.status === 'cancelled'
                    ? 'ghost'
                    : booking.status === 'serving'
                      ? 'e'
                      : 'warn';
              return (
                <div key={booking.id} className="card tight booking-card" style={{ background: 'rgba(255,255,255,.02)' }}>
                  <div className="bk-head">
                    <div className="li-t" style={{ fontSize: 13, fontWeight: 600 }}>
                      {booking.resourceName}
                    </div>
                    <span className="bk-status">
                      <Badge tone={tone}>{booking.statusLabel}</Badge>
                    </span>
                  </div>
                  <div className="text-tiny text-muted" style={{ marginTop: 5, lineHeight: 1.7 }}>
                    号源：{booking.slotLabel || '待排期'}
                    <br />
                    提交时间：{formatDateTime(booking.createdAt)}
                  </div>
                  {booking.note && (
                    <div className="data-tag" style={{ marginTop: 7 }}>
                      备注：{booking.note}
                    </div>
                  )}

                  {booking.timeline.length > 0 && (
                    <>
                      <div className="divider" />
                      <Timeline
                        items={booking.timeline.map((event, i) => ({
                          id: `${booking.id}-${String(i)}`,
                          date: formatDateTime(event.at),
                          title: event.text,
                          isCurrent: i === booking.timeline.length - 1,
                        }))}
                      />
                    </>
                  )}

                  {cancelable && (
                    <>
                      <div className="divider" />
                      {confirming ? (
                        <div className="row-flex" style={{ gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, color: 'var(--warn)' }}>确认取消该预约？号源将立即释放。</span>
                          <Button
                            small
                            variant="danger"
                            disabled={cancelBooking.isPending}
                            onClick={() => cancelBooking.mutate(booking.id)}
                          >
                            {cancelBooking.isPending ? '取消中…' : '确认取消'}
                          </Button>
                          <Button small onClick={() => setCancelConfirmId(null)}>
                            再想想
                          </Button>
                        </div>
                      ) : (
                        <Button small onClick={() => setCancelConfirmId(booking.id)}>
                          取消预约
                        </Button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ---------------- 资源准入机制 ---------------- */}
      <div className="note-src">
        <b>资源准入机制</b>：{admissionRule}
        <br />
        平台不直接开展临床诊疗，医疗服务由具备资质的合作医疗机构执行；页面所有号源状态由排期中心实时计算。
      </div>

      {/* ---------------- 预约确认弹层 ---------------- */}
      <Sheet
        open={bookingTarget !== null}
        title="确认预约"
        onClose={() => {
          if (!createBooking.isPending) closeBookingSheet();
        }}
      >
        {bookingTarget && bookingSlot && (
          <>
            <div className="li-t" style={{ fontSize: 13.5, fontWeight: 600 }}>
              {bookingTarget.resource.name}
            </div>
            <div className="text-tiny text-muted" style={{ marginTop: 4 }}>
              {bookingTarget.resource.institution} · {bookingTarget.resource.city}
            </div>

            <div className="divider" />

            <div className="row between">
              <span className="text-muted" style={{ fontSize: 11.5 }}>
                号源时间
              </span>
              <span className="mono-num" style={{ fontSize: 12.5, fontWeight: 600 }}>
                {formatSlot(bookingSlot.startAt)}
              </span>
            </div>
            <div className="row between" style={{ marginTop: 6 }}>
              <span className="text-muted" style={{ fontSize: 11.5 }}>
                时段标签
              </span>
              <span style={{ fontSize: 11.5 }}>{bookingSlot.label}</span>
            </div>
            <div className="row between" style={{ marginTop: 6 }}>
              <span className="text-muted" style={{ fontSize: 11.5 }}>
                剩余名额
              </span>
              <span style={{ fontSize: 11.5 }}>{bookingRemaining} / {bookingSlot.capacity}</span>
            </div>

            <div className="field" style={{ marginTop: 14 }}>
              <label className="field-label" htmlFor="svc-note">
                备注（可选，便于管家提前准备）
              </label>
              <textarea
                id="svc-note"
                className="field-input"
                rows={3}
                placeholder="例如：需安排无障碍通道、既往病史与用药情况、随行人数"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {/* 约满 / 过期时后端会返回具体原因，此处原样展示在弹层内 */}
            {sheetError && <div className="field-error">{sheetError}</div>}

            <Button
              block
              variant="primary"
              disabled={createBooking.isPending}
              onClick={() => {
                setSheetError(null);
                createBooking.mutate({
                  resourceId: bookingTarget.resource.id,
                  slotId: bookingSlot.id,
                  note: note.trim(),
                });
              }}
            >
              {createBooking.isPending ? '正在提交…' : '提交预约'}
            </Button>

            <div className="field-hint">
              提交后由专属健康管家在响应标准内受理；号源库存以服务端为准，名额满额时会提示改约。
            </div>
          </>
        )}
      </Sheet>
    </div>
  );
}
